import React, { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Download } from 'lucide-react';
import styled from 'styled-components';

import { StudentColors } from 'components/pages/StudentDetail/parts/studentColors';
import {
  downloadRosterCsv,
  IRosterCsvHeaders,
  IRosterExportRow,
} from 'core/utils/exportCsv';
import type { IStudentListRow } from 'core/utils/studentRoster';

interface IRosterExportButtonProps {
  /** The currently visible rows (after filter + search + sort), Req 21.1. */
  rows: IStudentListRow[];
  /** Finance-view permission. When false the control is not rendered (Req 21.5). */
  canExportFinance: boolean;
}

const toNumber = (value: number | string | null | undefined): number => {
  const num = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(num) ? num : 0;
};

/**
 * Roster export control for the Students list (Area 8, Req 21.1/21.3/21.5).
 *
 * Maps the currently visible rows onto the shared `IRosterExportRow` extended
 * with the `group` column and downloads a CSV with localized headers (Req 21.4).
 * Hidden entirely when the operator lacks finance-view permission (Req 21.5);
 * the underlying builder additionally has no side effect when no rows exist.
 */
export const RosterExportButton = memo(
  ({ rows, canExportFinance }: IRosterExportButtonProps) => {
    const { t } = useTranslation();

    const onExport = useCallback(() => {
      const exportRows: IRosterExportRow[] = rows.map((row) => {
        const budget = toNumber(row.budgetAmount);
        // The builder recomputes debt = max(0, budget − paid). A terminated row
        // must export debt 0 (Req 1.4), so feed paid = budget for those rows.
        const paid = row.terminated ? budget : toNumber(row.paidAmount);
        return {
          studentName: row.name,
          agreementTitle: row.contractTitle,
          group: row.groupName,
          currency: row.currency,
          budget,
          paid,
        };
      });

      const headers: IRosterCsvHeaders = {
        studentName: t('table.students.name'),
        agreementTitle: t('table.students.contractTitle'),
        group: t('table.students.group'),
        currency: t('table.students.currency'),
        budget: t('table.students.budgetAmount'),
        paid: t('table.students.paidAmount'),
        debt: t('table.students.debt'),
      };

      downloadRosterCsv(exportRows, headers, 'students-roster.csv');
    }, [rows, t]);

    if (!canExportFinance) return null;

    return (
      <Button type="button" onClick={onExport}>
        <Download size={14} strokeWidth={2} />
        <span>{t('pages.students.export')}</span>
      </Button>
    );
  },
);

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid ${StudentColors.border};
  border-radius: 8px;
  background: ${StudentColors.surface};
  color: ${StudentColors.accent};
  font: inherit;
  font-size: 13px;
  padding: 7px 14px;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: ${StudentColors.accentSurface};
  }
`;
